'use client'

import { E_EARN_VAULT_CHAIN_ID } from '@/config/euler-contracts'
import { getMarketGetAction } from '@/config/market-config'
import { paths } from '@/config/paths'
import { getTokenImageUrl } from '@/domain/euler'
import { useOpenDialog } from '@/domain/hooks/useDialog'
import { getDisplayTokenSymbol } from '@/domain/hooks/useDisplayToken'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { getEVaultHoldingsData, getEVaultHoldingsSortValue } from '@/features/portfolio'
import {
  type MarketDepositActionDialogProps,
  marketDepositActionDialogConfig,
} from '@/features/markets-overview/MarketDepositActionDialog'
import { marketGetActionDialogConfig } from '@/features/markets-overview/MarketGetActionDialog'
import { HoldingsCellBase } from '@/features/unified-earn/components/HoldingsCell'
import { Button } from '@/ui/atoms/button/Button'
import { Dash } from '@/ui/atoms/dash/Dash'
import { TokenImage } from '@/ui/atoms/token-image/TokenImage'
import { Info } from '@/ui/molecules/info/Info'
import { cn } from '@/ui/utils/style'
import { useBreakpoint } from '@/ui/utils/useBreakpoint'
import { useWindowVirtualizer } from '@tanstack/react-virtual'
import { ArrowRight, ChevronDown, ChevronUp, Sparkles } from 'lucide-react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { type KeyboardEvent, type MouseEvent, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useAccount } from '@/domain/hooks/useAccount'
import type { EVaultData } from '../logic/useEVaults'

interface EVaultListViewProps {
  vaults: EVaultData[]
}

type SortField = 'apy' | 'apy30d' | 'tvl' | 'holdings'
type SortDirection = 'asc' | 'desc'

function formatUsdCompact(value: number | undefined): string {
  if (value === undefined || value === 0) return '-'
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`
  return `$${value.toFixed(2)}`
}

function EVaultGetButton({ vault, displaySymbol }: { vault: EVaultData; displaySymbol: string }) {
  const openDialog = useOpenDialog()
  const getAction = getMarketGetAction('earn', [vault.address, vault.underlyingAsset])

  const openGetAction = (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!getAction) return

    openDialog(marketGetActionDialogConfig, {
      action: getAction,
      assetSymbol: displaySymbol,
      marketName: 'Earn Vaults',
    })
  }

  const stopRowKeyNav = (e: KeyboardEvent<HTMLButtonElement>) => {
    e.stopPropagation()
  }

  return getAction ? (
    <Button
      variant="yellow"
      size="sm"
      className="h-5 rounded-full px-1.5 font-bold text-[11px]"
      onClick={openGetAction}
      onKeyDown={stopRowKeyNav}
      prefixIcon={<Sparkles className="h-2.5 w-2.5" />}
    >
      {getAction.label}
    </Button>
  ) : null
}

function EVaultDepositButton({ vault }: { vault: EVaultData }) {
  const address = useAccount()
  const openDialog = useOpenDialog()

  const openDepositAction = (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    openDialog<Pick<MarketDepositActionDialogProps, 'vaultAddress' | 'chainId' | 'source'>>(
      marketDepositActionDialogConfig,
      {
        vaultAddress: vault.address,
        chainId: E_EARN_VAULT_CHAIN_ID,
        source: 'evault' as const,
      },
    )
  }

  const stopRowKeyNav = (e: KeyboardEvent<HTMLButtonElement>) => {
    e.stopPropagation()
  }

  if (!address) return null

  return (
    <Button
      variant="primary"
      size="sm"
      className="h-7 rounded-full px-3 text-xs"
      onClick={openDepositAction}
      onKeyDown={stopRowKeyNav}
    >
      Deposit
    </Button>
  )
}

export function EVaultListView({ vaults }: EVaultListViewProps) {
  const isDesktop = useBreakpoint('lg')

  if (!isDesktop) {
    return <EVaultMobileList vaults={vaults} />
  }

  return <EVaultDesktopTable vaults={vaults} />
}

function EVaultMobileList({ vaults }: { vaults: EVaultData[] }) {
  if (vaults.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-16">
        <p className="text-lg text-white/60">No vaults found</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {vaults.map((vault) => {
        const assetSymbol = vault.assetSymbol || vault.symbol
        const displaySymbol = getDisplayTokenSymbol(E_EARN_VAULT_CHAIN_ID, TokenSymbol(assetSymbol)) as string
        const tokenImage = getTokenImageUrl(vault.underlyingTokenInfo, assetSymbol)
        const fallbackLogo = vault.underlyingTokenInfo?.logoURI
        const tvlUsd = Number(vault.tvlUsd || 0)

        const destination = paths.lendDetails
          .replace(':chainId', E_EARN_VAULT_CHAIN_ID.toString())
          .replace(':vaultAddress', vault.address)

        return (
          <div
            key={vault.address}
            className="mx-auto w-full max-w-lg rounded-xl border border-white/[0.08] bg-white/[0.02] p-4"
          >
            <div className="flex w-full flex-col">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <TokenImage
                    src={tokenImage}
                    alt={displaySymbol}
                    width={32}
                    height={32}
                    className="h-8 w-8 shrink-0 rounded-xl object-cover"
                    fallbackSrc={fallbackLogo}
                    tokenAddress={vault.underlyingAsset}
                  />
                  <div className="flex flex-col gap-0.5">
                    <div className="flex min-w-0 items-center gap-1.5">
                      <span className="font-semibold text-foreground text-sm leading-tight">{displaySymbol}</span>
                      <EVaultGetButton vault={vault} displaySymbol={displaySymbol} />
                    </div>
                    <span className="text-dim-foreground text-xs leading-tight">{vault.name}</span>
                  </div>
                </div>
                <Link
                  href={destination}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground transition-opacity hover:opacity-80"
                >
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>

              <div className="mt-2 flex flex-col gap-2 border-white/[0.04] border-t pt-3">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-xs">APY</span>
                  <span className="font-medium text-foreground text-sm">
                    {vault.supplyAPY !== undefined ? `${vault.supplyAPY.toFixed(2)}%` : '0.00%'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-xs">30d APY</span>
                  <span className="font-medium text-foreground text-sm">
                    {vault.supplyApy30d != null && vault.supplyApy30d > 0 ? `${vault.supplyApy30d.toFixed(2)}%` : '-'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-xs">TVL</span>
                  {tvlUsd > 0 ? (
                    <span className="font-medium text-foreground text-sm">{formatUsdCompact(tvlUsd)}</span>
                  ) : (
                    <Dash />
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-xs">My Holdings</span>
                  <HoldingsCellBase {...getEVaultHoldingsData(vault)} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-xs">Action</span>
                  <EVaultDepositButton vault={vault} />
                </div>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

function EVaultDesktopTable({ vaults }: EVaultListViewProps) {
  const listRef = useRef<HTMLDivElement>(null)
  const [sortField, setSortField] = useState<SortField | null>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [listOffsetTop, setListOffsetTop] = useState(0)
  const router = useRouter()

  useLayoutEffect(() => {
    const updateOffset = () => {
      if (listRef.current) {
        setListOffsetTop(listRef.current.offsetTop)
      }
    }
    updateOffset()
    window.addEventListener('resize', updateOffset)
    return () => window.removeEventListener('resize', updateOffset)
  }, [])

  const sortedVaults = useMemo(() => {
    if (sortField === null) return vaults

    return [...vaults].sort((a, b) => {
      let aVal = 0
      let bVal = 0

      switch (sortField) {
        case 'apy':
          aVal = a.supplyAPY ?? 0
          bVal = b.supplyAPY ?? 0
          break
        case 'apy30d':
          aVal = a.supplyApy30d ?? 0
          bVal = b.supplyApy30d ?? 0
          break
        case 'tvl':
          aVal = Number(a.tvlUsd || 0)
          bVal = Number(b.tvlUsd || 0)
          break
        case 'holdings':
          aVal = getEVaultHoldingsSortValue(a)
          bVal = getEVaultHoldingsSortValue(b)
          break
      }

      return sortDirection === 'desc' ? bVal - aVal : aVal - bVal
    })
  }, [vaults, sortField, sortDirection])

  const virtualizer = useWindowVirtualizer({
    count: sortedVaults.length,
    estimateSize: () => 72,
    overscan: 5,
    scrollMargin: listOffsetTop,
  })

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'desc' ? 'asc' : 'desc'))
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
  }

  const SortIcon = ({ field }: { field: SortField }) => {
    const isActive = sortField === field
    return (
      <div className="-space-y-1 flex flex-col">
        <ChevronUp
          className={cn(
            'h-3 w-3',
            isActive && sortDirection === 'asc' ? 'text-foreground' : 'text-muted-foreground/40',
          )}
        />
        <ChevronDown
          className={cn(
            'h-3 w-3',
            isActive && sortDirection === 'desc' ? 'text-foreground' : 'text-muted-foreground/40',
          )}
        />
      </div>
    )
  }

  const gridCols = 'minmax(0,1.2fr) minmax(0,1fr) minmax(0,1fr) minmax(0,1fr) minmax(0,0.8fr) minmax(0,0.7fr)'

  return (
    <div className="flex flex-col rounded-2xl border border-border bg-panel">
      <div
        className="sticky top-12 z-10 grid items-center gap-3 rounded-t-2xl border-border border-b bg-card px-5 pt-5 pb-2 text-muted-foreground text-xs"
        style={{ gridTemplateColumns: gridCols }}
      >
        <div>Asset</div>
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            className={cn('flex items-center gap-1 hover:text-foreground', sortField === 'apy' && 'text-foreground')}
            onClick={() => handleSort('apy')}
          >
            APY <SortIcon field="apy" />
          </button>
          <Info size={12}>Current supply APY earned on deposited assets.</Info>
        </div>
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            className={cn('flex items-center gap-1 hover:text-foreground', sortField === 'apy30d' && 'text-foreground')}
            onClick={() => handleSort('apy30d')}
          >
            30d APY <SortIcon field="apy30d" />
          </button>
          <Info size={12}>30-day average supply APY.</Info>
        </div>
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            className={cn('flex items-center gap-1 hover:text-foreground', sortField === 'tvl' && 'text-foreground')}
            onClick={() => handleSort('tvl')}
          >
            TVL <SortIcon field="tvl" />
          </button>
          <Info size={12}>Total value currently deposited in this vault.</Info>
        </div>
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            className={cn(
              'flex items-center gap-1 hover:text-foreground',
              sortField === 'holdings' && 'text-foreground',
            )}
            onClick={() => handleSort('holdings')}
          >
            My Holdings <SortIcon field="holdings" />
          </button>
          <Info size={12}>Your total value across deposited positions and wallet balance.</Info>
        </div>
        <div />
      </div>

      <div
        ref={listRef}
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          position: 'relative',
        }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const vault = sortedVaults[virtualRow.index]
          if (!vault) return null

          const assetSymbol = vault.assetSymbol || vault.symbol
          const displaySymbol = getDisplayTokenSymbol(E_EARN_VAULT_CHAIN_ID, TokenSymbol(assetSymbol)) as string
          const tokenImage = getTokenImageUrl(vault.underlyingTokenInfo, assetSymbol)
          const fallbackLogo = vault.underlyingTokenInfo?.logoURI
          const tvlUsd = Number(vault.tvlUsd || 0)
          const isLastRow = virtualRow.index === sortedVaults.length - 1

          const destination = paths.lendDetails
            .replace(':chainId', E_EARN_VAULT_CHAIN_ID.toString())
            .replace(':vaultAddress', vault.address)

          const openDetails = () => {
            router.push(destination)
          }

          const handleRowKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
            if (e.key !== 'Enter') return
            e.preventDefault()
            openDetails()
          }

          return (
            <div
              key={vault.address}
              role="link"
              tabIndex={0}
              aria-label={`Open ${displaySymbol}`}
              onClick={openDetails}
              onKeyDown={handleRowKeyDown}
              className={cn(
                'absolute top-0 left-0 grid w-full cursor-pointer items-center gap-3 px-5 text-inherit no-underline outline-none transition-colors focus-visible:bg-white/[0.03] hover:bg-white/[0.02] focus-visible:ring-1 focus-visible:ring-primary/60 focus-visible:ring-inset',
                !isLastRow && 'border-white/5 border-b',
              )}
              style={{
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start - virtualizer.options.scrollMargin}px)`,
                gridTemplateColumns: gridCols,
              }}
            >
              <div className="flex items-center gap-3">
                <TokenImage
                  src={tokenImage}
                  alt={displaySymbol}
                  width={32}
                  height={32}
                  className="h-8 w-8 shrink-0 rounded-full"
                  fallbackSrc={fallbackLogo}
                  tokenAddress={vault.underlyingAsset}
                />
                <div className="flex min-w-0 flex-col gap-1">
                  <div className="flex min-w-0 items-center gap-1.5">
                    <span className="truncate font-semibold text-foreground text-sm">{displaySymbol}</span>
                    <EVaultGetButton vault={vault} displaySymbol={displaySymbol} />
                  </div>
                  <span className="truncate text-dim-foreground text-xs">{vault.name}</span>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2">
                <span className="font-medium text-foreground text-sm">
                  {vault.supplyAPY !== undefined ? `${vault.supplyAPY.toFixed(2)}%` : '0.00%'}
                </span>
              </div>

              <div className="flex items-center justify-end">
                {vault.supplyApy30d != null && vault.supplyApy30d > 0 ? (
                  <span className="font-medium text-foreground text-sm">{vault.supplyApy30d.toFixed(2)}%</span>
                ) : (
                  <Dash />
                )}
              </div>

              <div className="flex justify-end">
                {tvlUsd > 0 ? (
                  <span className="font-medium text-foreground text-sm">{formatUsdCompact(tvlUsd)}</span>
                ) : (
                  <Dash />
                )}
              </div>

              <div className="flex justify-end">
                <HoldingsCellBase {...getEVaultHoldingsData(vault)} />
              </div>

              <div className="flex justify-end">
                <EVaultDepositButton vault={vault} />
              </div>
            </div>
          )
        })}
      </div>

      {sortedVaults.length === 0 && (
        <div className="flex items-center justify-center p-8 text-muted-foreground">No vaults found</div>
      )}
    </div>
  )
}
